/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Logger } from "@azure/msal-browser";
import {
    ApplicationInsights,
    IExtendedConfiguration,
    IExtendedTelemetryItem,
} from "@microsoft/1ds-analytics-js";
import { PerformanceEvent, PerformanceEvents } from "./TelemetryUtils.js";

// Instrumentation Key for "MSALJS Tenant" Cluster
const instrumentationKey =
    "d634483c08244c1ca09af2b2d952c92e-ab2bba03-2ba3-49d8-a82c-ef6da750d8ab-7725";

/**
 * Returns boolean flag, indicating whether event payload is redacted
 */
export function isRedactedEvent(ratio: number): boolean {
    const randomNumber = Math.random();
    return randomNumber <= ratio;
}

export class PerformanceTelemetryReporter {
    private logger: Logger;
    private readonly eventsToEmit: Set<string>;
    private readonly appInsights: ApplicationInsights;
    readonly synthetic: boolean = false;
    private readonly redactionRatio = 0.1;

    constructor(
        logger: Logger,
        eventsToEmit: PerformanceEvents[],
        synthetic?: boolean
    ) {
        this.logger = logger;
        this.eventsToEmit = new Set(eventsToEmit);

        const appInsightsConfig: IExtendedConfiguration = {
            instrumentationKey,
            extensions: [],
            propertyConfiguration: {
                gpcDataSharingOptIn: false,
            },
            disableCookiesUsage: true,
        };

        this.appInsights = new ApplicationInsights();
        this.appInsights.initialize(appInsightsConfig, []);
        this.synthetic = synthetic || false;
        this.logger.verbose("PerformanceTelemetryReporter: Initialized");
    }

    sendCustomEvent(event: PerformanceEvent): void {
        if (!this.eventsToEmit.has(event.name)) {
            this.logger.verbose(
                `PerformanceTelemetryReporter: Skipping telemetry for ${event.name}`,
                event.correlationId
            );
            return;
        }

        let dataToEmit: PerformanceEvent;

        // Failed events are always captured to facilitate troubleshooting
        if (!event.success || isRedactedEvent(this.redactionRatio)) {
            dataToEmit = {
                ...event,
                appName: undefined, // Do not emit appName until privacy concerns are resolved
                synthetic: this.synthetic, // Synthetic events used for benchmarking purposes
            };
            this.logger.verbose(
                `PerformanceTelemetryReporter: Sending ${dataToEmit.name} for tracking`
            );
        } else {
            // Minimal set of fields for successful events to ensure proper reliability calculation.
            dataToEmit = {
                name: event.name,
                eventId: event.eventId,
                correlationId: event.correlationId,
                requestId: event.requestId,
                success: event.success,
                status: event.status,
                fromCache: event.fromCache,
                startPageVisibility: event.startPageVisibility,
                endPageVisibility: event.endPageVisibility,
                authority: event.authority,
                durationMs: event.durationMs,
                startTimeMs: event.startTimeMs,
                clientId: event.clientId,
                libraryName: event.libraryName,
                libraryVersion: event.libraryVersion,
                accountType: event.accountType,
                synthetic: this.synthetic,
                isBroker: event.isBroker,
                isBackground: event.isBackground,
            };

            this.logger.verbose(
                `PerformanceTelemetryReporter: Sending redacted ${dataToEmit.name} for tracking`
            );
        }

        const event1: IExtendedTelemetryItem = {
            name: event.name,
            data: dataToEmit as IExtendedTelemetryItem["data"],
        };

        this.appInsights.track(event1);
        if (event.name === PerformanceEvents.AcquireTokenPreRedirect) {
            this.appInsights.flush();
        }
    }
}
